/** The player a slot is currently showing. */
export const resolveEntry = (entry, swaps) => {
    const option = swaps[entry.entryId];
    if (!option)
        return entry;
    /* Spelled out rather than spread: a market card has no eaPlayerId, and a
       spread would keep the club card's. */
    return {
        entryId: entry.entryId,
        points: entry.points,
        player: option.player,
        position: option.position,
        price: option.price,
        source: option.source,
        untradeable: option.untradeable,
        eaPlayerId: option.eaPlayerId,
    };
};
/**
 * The solution as it currently stands, aggregates included. Costs and counts
 * are recomputed rather than carried over: swapping a market card for a club
 * one changes what the header says, and a stale total is worse than none.
 */
export const resolveSolution = (solution, swaps) => {
    if (Object.keys(swaps).length === 0)
        return solution;
    const brackets = solution.brackets.map((bracket) => ({
        ...bracket,
        players: bracket.players.map((entry) => resolveEntry(entry, swaps)),
    }));
    const entries = brackets.flatMap((bracket) => bracket.players);
    const market = entries.filter((entry) => entry.source === "MARKET");
    return {
        ...solution,
        brackets,
        totalValue: entries.reduce((sum, entry) => sum + entry.price, 0),
        transferCost: market.reduce((sum, entry) => sum + entry.price, 0),
        transferCount: market.length,
        ownedCount: entries.length - market.length,
    };
};
/** How many cards a slot is offered, and how many of them are club cards when the club has them. */
export const SLOT_OPTION_COUNT = 4;
export const SLOT_CLUB_OPTION_COUNT = 2;
/** The physical club cards the solution currently holds, so no slot is offered one twice. */
export const heldClubCards = (solution) => new Set(solution.brackets
    .flatMap((row) => row.players)
    .map((slot) => slot.eaPlayerId)
    .filter((id) => id !== undefined));
/** Which corner of the club a card is offered from first: duplicates, then SBC storage, club, transfer list. */
export const SOURCE_RANK = {
    DUPLICATES: 0,
    "SBC-STORAGE": 1,
    CLUB: 2,
    "TRANSFER-LIST": 3,
    MARKET: 4,
};
/** First occurrence wins, so what comes earlier in `cards` leads the result. */
const distinctBy = (cards, key) => {
    const seen = new Set();
    return cards.filter((card) => {
        const k = key(card);
        if (seen.has(k))
            return false;
        seen.add(k);
        return true;
    });
};
/**
 * The swap offer for one slot of a resolved solution: up to
 * `SLOT_CLUB_OPTION_COUNT` spare club cards, then the cheapest market cards
 * up to `SLOT_OPTION_COUNT` in all, so a user with no club sees four market
 * cards.
 *
 * The pool for the row's rating is the backend's options merged with every
 * card the solver picked at that rating in any of the set's solutions: the
 * solver picks by its own rule (untradeables first, within the fodder margin),
 * so its cards are often not among the backend's cheapest, and a card swapped
 * out of a slot must be back in every slot's offer whichever slot freed it.
 * Deriving the offer from the pool and the current solution alone makes that
 * automatic. The club offer is the pool minus the cards the solution holds,
 * by where the card sits (`SOURCE_RANK`) and then price; the market offer is
 * the pool minus the card in the slot, cheapest first. Sibling slots may show
 * the same market card — the picker buys duplicates when the pool is short.
 */
export const slotOptions = (solution, entry, options, 
/** The set's solutions before any swaps: where the solver's picks come from. */
solutions) => {
    const bracket = solution.brackets.find((row) => row.players.some((slot) => slot.entryId === entry.entryId));
    if (!bracket)
        return [];
    const pool = options[bracket.rating] ?? { club: [], market: [] };
    const picks = solutions
        .flatMap((base) => base.brackets)
        .filter((row) => row.rating === bracket.rating)
        .flatMap((row) => row.players);
    const held = heldClubCards(solution);
    const club = distinctBy([...picks, ...pool.club].filter((option) => option.eaPlayerId !== undefined && !held.has(option.eaPlayerId)), (option) => option.eaPlayerId)
        .sort((a, b) => SOURCE_RANK[a.source] - SOURCE_RANK[b.source] || a.price - b.price)
        .slice(0, SLOT_CLUB_OPTION_COUNT);
    const market = distinctBy([...picks, ...pool.market].filter((option) => option.eaPlayerId === undefined &&
        option.player.resourceId !== entry.player.resourceId), (option) => option.player.resourceId).sort((a, b) => a.price - b.price);
    return [...club, ...market].slice(0, SLOT_OPTION_COUNT);
};
//# sourceMappingURL=swaps.js.map